import React, { useState, useMemo, useEffect } from 'react';
import { Lock, Unlock, RefreshCw, AlertTriangle, Zap } from 'lucide-react';
import apiClient from '../../config/api.config';
import { useGetMatieresByFiltersQuery } from '../../services/matieresApi';
import { useGetChoixFormulaireQuery } from '../../services/etudiantsApi';
import { useGetAnneeActiveQuery, useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import SaisieMatricielleExamens from './SaisieMatricielleExamens';
import SaisieIndividuelleExamens from './SaisieIndividuelleExamens';
import SaisieLigneExamens from './SaisieLigneExamens';
import SaisieParCodeCopieExamens from './SaisieParCodeCopieExamens';
import SaisieParMatiereExamens from './SaisieParMatiereExamens';
import ActionsRapidesExamens from './ActionsRapidesExamens';
import OutilsAvances from '../../components/NotesTools/OutilsAvances';
import ScrollToTop from '../../components/ui/ScrollToTop';

type ModeSaisie = 'matricielle' | 'individuelle' | 'ligne' | 'code_copie' | 'par_matiere';
type VueActive = 'saisie' | 'par_matiere' | 'actions_rapides' | 'outils';

const SaisieNotesExamens: React.FC = () => {
  const { data: choixFormulaire, isLoading: loadingChoix } = useGetChoixFormulaireQuery();
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: annees } = useGetAnneesAcademiquesQuery();
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });

  const [mode, setMode] = useState<ModeSaisie>('matricielle');
  const [vueActive, setVueActive] = useState<VueActive>('saisie');
  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [niveau, setNiveau] = useState<string>('');
  const [classe, setClasse] = useState<string>('');
  const [poleFilter, setPoleFilter] = useState<string>('');
  const [anneeAcademique, setAnneeAcademique] = useState('');
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [estRattrapage, setEstRattrapage] = useState<boolean>(false);
  const [parametresValides, setParametresValides] = useState(false);
  const [recalculing, setRecalculing] = useState(false);
  const [recalcMsg, setRecalcMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleRecalculerClasse = async () => {
    if (!classe || recalculing) return;
    setRecalculing(true);
    setRecalcMsg(null);
    try {
      // Fix 2026-09-09 : passer `semestre` (1=impairs / 2=pairs) — évite
      // d'écraser les résultats de l'autre semestre déjà publiés lors d'un
      // changement de mode arithmétique/pondération.
      const semestreLocal = semestre === 'S1' ? 1 : 2;
      const res = await apiClient.post('/portail/admin/recalculer-classe/', {
        classe,
        semestre: semestreLocal,
      });
      const d = res.data;
      setRecalcMsg({
        type: 'success',
        text: `Recalcul lancé pour ${d.nb_etudiants || 0} étudiants de ${classe} (en arrière-plan, mis à jour sous 1-2 min).`,
      });
      setTimeout(() => setRecalcMsg(null), 6000);
    } catch (err: any) {
      setRecalcMsg({
        type: 'error',
        text: err?.response?.data?.detail || 'Erreur lors du recalcul',
      });
    } finally {
      setRecalculing(false);
    }
  };

  // Verrouillage matière : permet de garder la matière lors d'un changement
  // de classe (utile pour saisir la même matière sur plusieurs classes
  // successivement). On retient le NOM (pas l'id) car l'id change selon
  // l'option de la classe.
  const [matiereLocked, setMatiereLocked] = useState(false);
  const [lockedMatiereName, setLockedMatiereName] = useState<string | null>(null);

  // Année de saisie : SUIT le sélecteur d'année GLOBAL (localStorage
  // `annee_academique`). Fix 2026-10-15 : on ne force plus l'année active —
  // sinon la saisie des examens des années antérieures était impossible. On ne
  // réécrit pas localStorage au unmount : le sélecteur global reste la vérité.
  useEffect(() => {
    const storedAnnee = localStorage.getItem('annee_academique');
    if (storedAnnee) {
      setAnneeAcademique(storedAnnee);
    } else if (anneeActive) {
      setAnneeAcademique(anneeActive.libelle);
    }
  }, [anneeActive]);

  const handleChangeAnneeSaisie = (nouvelle: string) => {
    setAnneeAcademique(nouvelle);
    const cible = (annees ?? []).find(a => a.libelle === nouvelle);
    if (nouvelle) {
      localStorage.setItem('annee_academique', nouvelle);
      if (cible?.id) localStorage.setItem('annee_academique_id', String(cible.id));
    }
  };

  const estAnneeActive = !!anneeActive && anneeAcademique === anneeActive.libelle;

  // Niveaux depuis choixFormulaire
  const niveaux = useMemo(() => {
    if (!choixFormulaire?.niveaux) return [];
    return choixFormulaire.niveaux.filter(n => n.count > 0).map(n => n.code);
  }, [choixFormulaire]);

  // Fix 2026-09-08 : map code → count depuis choixFormulaire.classes pour
  // afficher le vrai nombre d'étudiants dans la liste (au lieu de « (0) »
  // hardcodé). choixFormulaire.classes.code inclut parfois un suffixe (CS),
  // on lookup à la fois par code exact et par préfixe.
  const classCountMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const c of (choixFormulaire?.classes ?? [])) {
      m[c.code] = (m[c.code] || 0) + (c.count || 0);
    }
    return m;
  }, [choixFormulaire]);

  // Classes filtrées par niveau et pôle
  const classes = useMemo(() => {
    if (!niveau) return [];
    let filtered = allClasses.filter(c => c.niveau === niveau);
    if (poleFilter) {
      filtered = filtered.filter(c => c.pole_nom === poleFilter);
    }
    return filtered.map(c => ({
      code: c.code,
      label: `${c.code} — ${c.option_nom}`,
      count: classCountMap[c.code] || 0,
    }));
  }, [allClasses, niveau, poleFilter, classCountMap]);

  // Option de la classe sélectionnée
  const classeSelectionnee = useMemo(() => {
    if (!classe) return null;
    return allClasses.find(c => c.code === classe) || null;
  }, [allClasses, classe]);

  // Matières filtrées STRICTEMENT par classe (utilise Option.code dérivé du code
  // de classe — évite le bug AI/AII où Classe.option_id peut être inversé)
  // ET par semestre sélectionné : la sidebar ne montre que les matières du
  // semestre en cours. Matiere.semestre est numéroté 1→6 selon le LMD :
  //   L1 S1=1, L1 S2=2 | L2 S1=3, L2 S2=4 | L3 S1=5, L3 S2=6.
  //
  // Fallback niveau : on dérive du code de classe (ex. `L1GRH-CS` → `L1`) si
  // `niveau` n'est pas sélectionné dans les filtres, sinon changer S1↔S2 ne
  // filtrait pas la liste.
  const niveauEffectifExam = niveau || (() => {
    for (const nv of ['L1', 'L2', 'L3', 'M1', 'M2']) {
      if (classe.toUpperCase().startsWith(nv)) return nv;
    }
    return '';
  })();
  const niveauOffset = niveauEffectifExam === 'L2' ? 2
    : niveauEffectifExam === 'L3' ? 4
    : niveauEffectifExam === 'M1' ? 6
    : niveauEffectifExam === 'M2' ? 8
    : 0;
  const semestreNum = niveauOffset + (semestre === 'S1' ? 1 : 2);
  const semestreLocal = semestre === 'S1' ? 1 : 2;
  // Fix 2026-10-15 : ne PAS filtrer le semestre côté serveur (le serveur fait un
  // match exact, or les schémas ARCHIVÉS stockent matieres.semestre en LOCAL 1/2
  // alors que l'année active l'a en GLOBAL 5/6 → « Aucune matière » sur une année
  // antérieure). On charge toutes les matières de la classe (année dans la clé de
  // cache RTK) et on filtre le semestre côté client en tolérant local ET global.
  const { data: matieres, isLoading: loadingMatieres } = useGetMatieresByFiltersQuery(
    { classe: classe || undefined, annee_academique: anneeAcademique || undefined },
    { skip: !classe || !niveauEffectifExam }
  );
  const matieresDuSemestre = useMemo(() => {
    if (!Array.isArray(matieres)) return [];
    return matieres.filter((m: any) => {
      const s = Number(m.semestre);
      return s === semestreNum || s === semestreLocal;
    });
  }, [matieres, semestreNum, semestreLocal]);

  const selectedMatiere = matieresDuSemestre.find((m) => m.id === matiereId);

  // Quand la liste des matieres change (= classe a change), si la matiere est
  // verrouillee, on cherche une matiere homonyme dans la nouvelle classe
  useEffect(() => {
    if (!matiereLocked || !lockedMatiereName) return;
    if (matieresDuSemestre.length === 0) return;
    const match = matieresDuSemestre.find((m: any) => m.nom_matiere === lockedMatiereName);
    if (match && match.id !== matiereId) {
      setMatiereId(match.id);
    } else if (!match && matiereId !== null) {
      setMatiereId(null);
    }
  }, [matieresDuSemestre, matiereLocked, lockedMatiereName]);

  const toggleMatiereLock = () => {
    if (matiereLocked) {
      setMatiereLocked(false);
      setLockedMatiereName(null);
    } else {
      if (selectedMatiere) {
        setLockedMatiereName(selectedMatiere.nom_matiere);
        setMatiereLocked(true);
      }
    }
  };

  const handleValiderParametres = () => {
    if (matiereId && niveau && classe && anneeAcademique && semestre) {
      setParametresValides(true);
    }
  };

  // Fix 2026-09-08 : raccourci « Aller directement à la saisie » — permet
  // d'entrer dans la fenêtre de saisie sans avoir configuré la matière.
  // L'utilisateur choisira ensuite classe/matière depuis la barre sticky
  // ou la sidebar des matières. Il faut au minimum le niveau + classe pour
  // que la barre sticky ait un contexte.
  const handleAllerDirectSaisie = () => {
    if (niveau && classe && anneeAcademique) {
      setParametresValides(true);
    }
  };

  const handleReinitialiser = () => {
    setParametresValides(false);
    setMatiereId(null);
    setNiveau('');
    setClasse('');
    setMatiereLocked(false);
    setLockedMatiereName(null);
  };

  // Recherche dans la sidebar matières (filtrage local)
  const [matiereSearchSidebar, setMatiereSearchSidebar] = useState('');
  const matieresSidebar = useMemo(() => {
    if (!matiereSearchSidebar.trim()) return matieresDuSemestre;
    const q = matiereSearchSidebar.toLowerCase();
    return matieresDuSemestre.filter((m: any) =>
      (m.nom_matiere || '').toLowerCase().includes(q) ||
      (m.code_matiere || '').toLowerCase().includes(q)
    );
  }, [matieresDuSemestre, matiereSearchSidebar]);

  return (
    <div className={`${parametresValides ? 'max-w-screen-2xl' : 'max-w-7xl'} mx-auto px-2 sm:px-4 lg:px-6 py-6`}>
      {/* En-tête */}
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Saisie des Notes d'Examens</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Saisissez les notes d'examens ou utilisez les actions rapides
          </p>
        </div>
        {/* Sélecteur année (bascule sur année archivée possible) */}
        <div className="flex items-center gap-2 mt-2">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Année :</label>
          <select
            value={anneeAcademique}
            onChange={(e) => handleChangeAnneeSaisie(e.target.value)}
            className={`text-sm border rounded px-2 py-1.5 ${
              estAnneeActive
                ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                : 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 font-semibold'
            }`}
          >
            {(annees ?? []).map(a => (
              <option key={a.id} value={a.libelle}>
                {a.libelle}{a.est_active ? ' (active)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bandeau avertissement */}
      {!estAnneeActive && anneeAcademique && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 flex items-start gap-3 text-sm">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-amber-900 dark:text-amber-100">
            <div className="font-semibold">
              Mode saisie année ARCHIVÉE — {anneeAcademique}
            </div>
            <div className="text-xs mt-1">
              Toutes les notes seront enregistrées dans le schéma {anneeAcademique}, PAS dans l'année active.
              Repositionnez sur {anneeActive?.libelle || 'l\'année active'} avant de reprendre la saisie courante.
            </div>
          </div>
          {anneeActive?.libelle && (
            <button
              onClick={() => handleChangeAnneeSaisie(anneeActive.libelle)}
              className="ml-auto text-xs px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white font-medium whitespace-nowrap"
            >
              Revenir à {anneeActive.libelle}
            </button>
          )}
        </div>
      )}

      {/* Toggle Saisie vs Actions Rapides */}
      <div className="mb-6 inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button
          onClick={() => setVueActive('saisie')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            vueActive === 'saisie'
              ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          Saisie des notes
        </button>
        <button
          onClick={() => setVueActive('par_matiere')}
          title="Verrouille une matière, puis cherche chaque étudiant par nom ou matricule — aucune classe à choisir"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            vueActive === 'par_matiere'
              ? 'bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          Par matière (nom)
        </button>
        <button
          onClick={() => setVueActive('actions_rapides')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            vueActive === 'actions_rapides'
              ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          Actions rapides
        </button>
        <button
          onClick={() => setVueActive('outils')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            vueActive === 'outils'
              ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-300 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
          }`}
        >
          Outils avancés
        </button>
      </div>

      {/* Fix 2026-10-02 : la saisie par matière choisit elle-même sa matière et
          cherche l'étudiant par son nom. Elle était rangée parmi les modes de
          saisie, donc enfermée derrière l'écran de configuration qui exige un
          niveau ET une classe — alors qu'elle n'en a justement pas besoin.
          Elle devient une vue à part entière, accessible dès l'arrivée. */}
      {vueActive === 'par_matiere' ? (
        <SaisieParMatiereExamens anneeAcademique={anneeAcademique} />
      ) : vueActive === 'outils' ? (
        <OutilsAvances variante="examens" anneeAcademique={anneeAcademique} />
      ) : vueActive === 'actions_rapides' ? (
        <ActionsRapidesExamens anneeAcademique={anneeAcademique} />
      ) : !parametresValides ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Configuration</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Sélectionnez le niveau, la classe, la matière et le semestre
          </p>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Pôle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pôle
              </label>
              <select
                value={poleFilter}
                onChange={(e) => {
                  setPoleFilter(e.target.value);
                  setClasse('');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Tous les pôles</option>
                {['Polytechnique', 'Commerce', 'Juridique'].map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            {/* Niveau */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Niveau <span className="text-red-500">*</span>
              </label>
              <select
                value={niveau}
                onChange={(e) => {
                  setNiveau(e.target.value);
                  setClasse('');
                  setMatiereId(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">-- Sélectionner --</option>
                {niveaux.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {/* Classe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Classe <span className="text-red-500">*</span>
              </label>
              <select
                value={classe}
                onChange={(e) => {
                  setClasse(e.target.value);
                  setMatiereId(null);
                }}
                disabled={!niveau}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">-- Sélectionner --</option>
                {classes.map((c) => (
                  <option key={c.code} value={c.code}>{c.label} ({c.count})</option>
                ))}
              </select>
            </div>

            {/* Fix 2026-09-08 : Semestre AVANT Matière — les matières
                affichées sont filtrées par semestre, donc l'ordre logique
                est Classe → Semestre → Matière. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Semestre <span className="text-red-500">*</span>
              </label>
              <select
                value={semestre}
                onChange={(e) => {
                  setSemestre(e.target.value as 'S1' | 'S2');
                  // Reset matière : la nouvelle liste sera pour le nouveau semestre
                  setMatiereId(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="S1">Semestre 1</option>
                <option value="S2">Semestre 2</option>
              </select>
            </div>

            {/* Matière */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Matière <span className="text-red-500">*</span>
                {classe && (
                  <span className="ml-1 text-[10px] text-gray-500 dark:text-gray-400 font-normal">
                    ({matieresDuSemestre.length} matières en {semestre})
                  </span>
                )}
              </label>
              {loadingMatieres || loadingChoix ? (
                <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 dark:text-gray-300">
                  Chargement...
                </div>
              ) : (
                <select
                  value={matiereId || ''}
                  onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!classe}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner --</option>
                  {matieresDuSemestre.map((matiere) => (
                    <option key={matiere.id} value={matiere.id}>
                      {matiere.code_matiere} - {matiere.nom_matiere}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Session */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Session
              </label>
              <select
                value={estRattrapage ? 'rattrapage' : 'ordinaire'}
                onChange={(e) => setEstRattrapage(e.target.value === 'rattrapage')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="ordinaire">Session Ordinaire</option>
                <option value="rattrapage">Session Rattrapage</option>
              </select>
            </div>
          </div>

          {classe && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
              <span className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Classe sélectionnée :</strong> {classe}
                {estRattrapage && <span className="ml-2 text-orange-600 dark:text-orange-400">(Session Rattrapage)</span>}
              </span>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button
              onClick={handleValiderParametres}
              disabled={!matiereId || !niveau || !classe}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Valider et continuer
            </button>
            {/* Fix 2026-09-08 : raccourci pour tomber direct dans la fenêtre
                de saisie sans avoir à choisir la matière ici. L'utilisateur
                choisit ensuite classe + matière depuis la barre sticky. */}
            <button
              onClick={handleAllerDirectSaisie}
              disabled={!niveau || !classe}
              title="Aller à la fenêtre de saisie sans configurer la matière — vous la choisirez dans la sidebar."
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Zap size={14} />
              Ouvrir la saisie (choisir matière plus tard)
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Le raccourci exige uniquement Niveau + Classe.
            </span>
          </div>
        </div>
      ) : (
        <>
          {/* Barre contextuelle compacte avec SÉLECTEURS RAPIDES (sticky) */}
          <div className="sticky top-0 z-20 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-3 py-2 mb-4 shadow-sm">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Sélecteur Classe (rapide) */}
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold uppercase">Classe</label>
                <select
                  value={classe}
                  onChange={(e) => {
                    setClasse(e.target.value);
                    // Si la matière est verrouillée, le useEffect tentera de retrouver
                    // une matière homonyme dans la nouvelle classe.
                    if (!matiereLocked) setMatiereId(null);
                  }}
                  className="px-2 py-1 text-xs font-bold bg-white dark:bg-gray-700 text-blue-900 dark:text-blue-100 border border-blue-300 dark:border-blue-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {classes.map(c => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
              </div>

              {/* Sélecteur Semestre (rapide) */}
              <div className="flex items-center gap-1.5">
                <label className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold uppercase">Sem.</label>
                <select
                  value={semestre}
                  onChange={(e) => setSemestre(e.target.value as 'S1' | 'S2')}
                  className="px-2 py-1 text-xs bg-white dark:bg-gray-700 text-blue-900 dark:text-blue-100 border border-blue-300 dark:border-blue-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="S1">S1</option>
                  <option value="S2">S2</option>
                </select>
              </div>

              {/* Toggle Rattrapage */}
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={estRattrapage}
                  onChange={(e) => setEstRattrapage(e.target.checked)}
                  className="rounded"
                />
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${estRattrapage ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'}`}>
                  {estRattrapage ? 'Rattrapage' : 'Ordinaire'}
                </span>
              </label>

              <span className="text-[10px] text-blue-700 dark:text-blue-300">
                {anneeAcademique}
              </span>

              {/* Bouton Recalculer la classe */}
              <button
                type="button"
                onClick={handleRecalculerClasse}
                disabled={!classe || recalculing}
                title={`Recalculer les résultats (ECUE/UE/Semestre) pour tous les étudiants de ${classe || 'la classe'}. À utiliser après avoir saisi/corrigé des notes.`}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded transition-colors"
              >
                <RefreshCw size={11} className={recalculing ? 'animate-spin' : ''} />
                {recalculing ? 'Recalcul…' : 'Recalculer classe'}
              </button>

              {/* Sélecteur Matière (raccourci sticky) */}
              {matieresDuSemestre.length > 0 && (
                <div className="flex items-center gap-1.5 flex-1 min-w-[200px] border-l border-blue-300 dark:border-blue-700 pl-2 ml-1">
                  <label className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold uppercase whitespace-nowrap">Matière</label>
                  <select
                    value={matiereId || ''}
                    onChange={(e) => setMatiereId(e.target.value ? parseInt(e.target.value) : null)}
                    className="flex-1 min-w-0 px-2 py-1 text-xs font-medium bg-white dark:bg-gray-700 text-blue-900 dark:text-blue-100 border border-blue-300 dark:border-blue-700 rounded truncate focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— Choisir —</option>
                    {matieresDuSemestre.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.code_matiere} — {m.nom_matiere}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={toggleMatiereLock}
                    disabled={!matiereLocked && !matiereId}
                    title={matiereLocked
                      ? `Déverrouiller (matière fixée : ${lockedMatiereName})`
                      : 'Verrouiller la matière (conservée lors du changement de classe)'}
                    className={`p-1 rounded transition-colors ${
                      matiereLocked
                        ? 'bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-600'
                        : 'text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                    }`}
                  >
                    {matiereLocked ? <Lock size={14} /> : <Unlock size={14} />}
                  </button>
                  {matiereLocked && (
                    <span className="text-[10px] text-amber-700 dark:text-amber-300 whitespace-nowrap">
                      verrouillée
                    </span>
                  )}
                </div>
              )}

              <button
                onClick={handleReinitialiser}
                className="text-[10px] text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 underline whitespace-nowrap ml-auto"
                title="Réinitialiser tous les paramètres"
              >
                ↻ Reset
              </button>
            </div>
            {recalcMsg && (
              <div className={`mt-1 text-[10px] px-2 py-1 rounded ${
                recalcMsg.type === 'success'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {recalcMsg.text}
              </div>
            )}
          </div>

          {/* Layout 2 colonnes : sidebar matières + zone saisie (réduite pour éviter débordement) */}
          <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-3">
            {/* ─── SIDEBAR MATIÈRES ─── */}
            <aside className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden self-start lg:sticky lg:top-20">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center justify-between">
                  Matières de {classe}
                  <span className="text-xs font-normal text-gray-500 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                    {matieresDuSemestre.length}
                  </span>
                </h3>
                <input
                  type="text"
                  value={matiereSearchSidebar}
                  onChange={(e) => setMatiereSearchSidebar(e.target.value)}
                  placeholder="Rechercher une matière..."
                  className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                {loadingMatieres ? (
                  <div className="p-4 text-center text-xs text-gray-400">Chargement…</div>
                ) : matieresSidebar.length === 0 ? (
                  <div className="p-4 text-center text-xs text-gray-400">
                    {matiereSearchSidebar ? 'Aucune matière trouvée' : 'Aucune matière'}
                  </div>
                ) : (
                  matieresSidebar.map((m: any) => {
                    const active = m.id === matiereId;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setMatiereId(m.id)}
                        className={`w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 transition-colors ${
                          active
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-600'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/40 border-l-4 border-l-transparent'
                        }`}
                      >
                        <div className="font-mono text-[11px] text-gray-500 dark:text-gray-400">
                          {m.code_matiere}
                        </div>
                        <div className={`text-sm font-medium ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}>
                          {m.nom_matiere}
                        </div>
                        {(m.enseignant_nom || m.enseignant) && (
                          <div className="text-[11px] text-gray-400 truncate">
                            {m.enseignant_nom || m.enseignant}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* ─── ZONE SAISIE ─── */}
            <div>

          {/* Onglets des modes de saisie */}
          <div className="mb-6">
            <nav className="flex space-x-4" aria-label="Modes de saisie">
              <button
                onClick={() => setMode('matricielle')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'matricielle'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                }`}
              >
                Mode 1: Saisie Matricielle
              </button>
              <button
                onClick={() => setMode('individuelle')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'individuelle'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                }`}
              >
                Mode 2: Saisie Individuelle
              </button>
              <button
                onClick={() => setMode('ligne')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'ligne'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                }`}
              >
                Mode 3: Ligne par Ligne
              </button>
              <button
                onClick={() => setMode('code_copie')}
                title="Saisis le code de la copie, l'étudiant + matière sont retrouvés automatiquement"
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'code_copie'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                }`}
              >
                Mode 4: Par Code de Copie
              </button>
              <button
                onClick={() => setMode('par_matiere')}
                title="Verrouille une matière, puis cherche chaque étudiant par nom ou matricule — toutes classes confondues"
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'par_matiere'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                }`}
              >
                Mode 5: Par Matière (recherche nom)
              </button>
            </nav>
          </div>

          {/* Composant de saisie selon le mode */}
          <div>
            {mode === 'matricielle' && matiereId && classe && (
              <SaisieMatricielleExamens
                classe={classe}
                matiereId={matiereId}
                anneeAcademique={anneeAcademique}
                semestre={semestre}
                estRattrapage={estRattrapage}
              />
            )}
            {mode === 'individuelle' && matiereId && (
              <SaisieIndividuelleExamens
                matiereId={matiereId}
                anneeAcademique={anneeAcademique}
                semestre={semestre}
                classe={classe}
                estRattrapage={estRattrapage}
              />
            )}
            {mode === 'ligne' && matiereId && (
              <SaisieLigneExamens
                matiereId={matiereId}
                anneeAcademique={anneeAcademique}
                semestre={semestre}
                classe={classe}
                estRattrapage={estRattrapage}
              />
            )}
            {/* La saisie par matière choisit elle-même sa matière et cherche
                l'étudiant par nom : elle ne dépend ni de `matiereId` ni de la
                classe sélectionnée dans la barre latérale. */}
            {mode === 'par_matiere' && (
              <SaisieParMatiereExamens anneeAcademique={anneeAcademique} />
            )}
            {mode === 'code_copie' && matiereId && (
              <SaisieParCodeCopieExamens
                matiereId={matiereId}
                anneeAcademique={anneeAcademique}
                semestre={semestre}
                classe={classe}
                estRattrapage={estRattrapage}
              />
            )}
            {/* Fix 2026-09-08 : placeholder quand aucune matière (utile
                pour le raccourci « Ouvrir la saisie »). L'utilisateur
                choisit une matière via la barre sticky ou la sidebar. */}
            {!matiereId && classe && mode !== 'par_matiere' && (
              <div className="text-center py-12 px-4 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                <div className="text-4xl mb-3">📋</div>
                <div className="font-semibold text-gray-800 dark:text-gray-100 mb-1">
                  Choisissez une matière pour commencer
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Utilisez le sélecteur « Matière » en haut de page ou la sidebar
                  {' '}pour la classe <strong>{classe}</strong>, semestre {semestre}.
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        </>
      )}

      <ScrollToTop threshold={500} />
    </div>
  );
};

export default SaisieNotesExamens;
